import { Alert, Box, Stack, Tooltip, Typography } from '@mui/material';
import { useFeatureEnabledForConfig } from 'api/configurations';
import { useCurrentStep } from 'api/guide';
import ValidationErrorsAlert from 'app/ValidationErrorsAlert';
import MDRenderer from 'components/MDRenderer/MDRenderer';
import GroupsAttributeMapping from 'components/SSOConfigFields/GroupsAttributeMapping';
import IdPInputField from 'components/SSOConfigFields/IdPInputField';
import SPDataField from 'components/SSOConfigFields/SPDataField';
import SPSCIMTokenField from 'components/SSOConfigFields/SPSCIMTokenField';
import UserAttributeMapping from 'components/SSOConfigFields/UserAttributeMapping';
import VerifyDomainInput from 'components/DomainVerification/VerifyDomainInput/VerifyDomainInput';
import { ROUTES } from 'constants/routes';
import { GuideType, MdComponentNames } from 'consts';
import { useIdPConfigContext } from 'contexts/IdPConfigContext';
import {
	useCurrentStepValidation,
	useSSOConfigCurrentFieldsInitialized
} from 'contexts/SSOConfigFieldsContext';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { S4Feature } from 'types/api';
import { useNav } from 'utils/navigation';
import SCIMContinueButton from './SCIMContinueButton';
import SSOContinueButton from './SSOContinueButton';
import XAAContinueButton from './XAAContinueButton';
import XAASavedConfirmation from './XAASavedConfirmation';

const mdOverrides = {
	[MdComponentNames.IdPInputField]: { component: IdPInputField },
	[MdComponentNames.SPDataField]: { component: SPDataField },
	[MdComponentNames.UserAttributes]: { component: UserAttributeMapping },
	[MdComponentNames.GroupsAttribute]: { component: GroupsAttributeMapping },
	[MdComponentNames.SPSCIMTokenField]: { component: SPSCIMTokenField },
	[MdComponentNames.VerifyDomainInput]: { component: VerifyDomainInput }
};

// Which suite feature each guide belongs to. SSO is not in here on purpose: setting up the identity
// provider is exactly what an authentication-only connection is for.
const GUIDE_FEATURE: Partial<Record<GuideType, S4Feature>> = {
	[GuideType.SCIM]: S4Feature.Scim,
	[GuideType.XAA]: S4Feature.Xaa
};

const IdPGuideStepPage: React.FC = () => {
	const { guideType, isSubTenant } = useIdPConfigContext();

	const { data: currentStepData } = useCurrentStep();

	const fieldsInitialized = useSSOConfigCurrentFieldsInitialized();

	const { fieldsErrors, fieldsUniqueErrorMessages } =
		useCurrentStepValidation();

	// XAA has no test step; after the final save the admin stays on the guide and sees a
	// saved confirmation with a Done button (mirroring SSO's post-save TestingPage) rather
	// than being silently redirected home.
	const [xaaSaved, setXaaSaved] = useState(false);

	// The saved confirmation is transient to the save action. IdPGuideStepPage is mounted
	// once (registered in AuthenticatedRoutes) and reused across guide-step param changes,
	// so this local state would otherwise persist and freeze the confirmation on an
	// unrelated step - reset it on any guide navigation.
	const { pathname } = useLocation();
	useEffect(() => {
		setXaaSaved(false);
	}, [pathname]);

	// A guide can be reached by URL - a bookmark, a shared link, the ?target= parameter - without
	// passing the sidebar that hides it, so the page has to turn away a guide this connection has no
	// use for rather than rely on nothing having linked to it. Re-checked on every render, so it
	// still fires when the configuration arrives after the page has mounted.
	const nav = useNav();
	const guideFeature = guideType ? GUIDE_FEATURE[guideType] : undefined;
	// Falls back to Sso, which is never one of the hidden features, so an unmapped guide type asks a
	// real question and always gets yes rather than being passed undefined.
	const { enabled: guideApplies } = useFeatureEnabledForConfig(
		guideFeature ?? S4Feature.Sso
	);
	useEffect(() => {
		if (!guideApplies) {
			nav(ROUTES.home);
		}
	}, [guideApplies, nav]);

	if (guideType === GuideType.XAA && xaaSaved) {
		return <XAASavedConfirmation />;
	}

	return (
		<Stack gap={3}>
			{currentStepData && fieldsInitialized && (
				<>
					<Typography variant="h2">{currentStepData.step.title}</Typography>
					<MDRenderer
						md={currentStepData.step.content}
						overrides={mdOverrides}
					/>
					<Stack gap={3}>
						{!isEmpty(fieldsErrors) && (
							<ValidationErrorsAlert messages={fieldsUniqueErrorMessages} />
						)}
						{isSubTenant && (
							<Alert
								data-testid="sub-tenant-info-alert"
								variant="outlined"
								severity="info"
								sx={{ whiteSpace: 'pre', textWrap: 'auto' }}
							>
								{intl.tr('ssoConfig.guide.subtenant.info')}
							</Alert>
						)}
						<Stack alignItems="end">
							<Tooltip
								sx={{ whiteSpace: 'pre' }}
								slotProps={{
									popper: { sx: { whiteSpace: 'pre', textWrap: 'wrap' } }
								}}
								title={fieldsUniqueErrorMessages.join('\n')}
							>
								<Box>
									{/* eslint-disable-next-line no-nested-ternary */}
									{guideType === GuideType.SCIM ? (
										<SCIMContinueButton disabled={!isEmpty(fieldsErrors)} />
									) : guideType === GuideType.XAA ? (
										<XAAContinueButton
											disabled={!isEmpty(fieldsErrors)}
											onSaved={() => setXaaSaved(true)}
										/>
									) : (
										<SSOContinueButton disabled={!isEmpty(fieldsErrors)} />
									)}
								</Box>
							</Tooltip>
						</Stack>
					</Stack>
				</>
			)}
		</Stack>
	);
};

export default IdPGuideStepPage;
